import { ReactNode } from "react";
import { useTrialProtection } from "@/hooks/useTrialProtection";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

interface TrialProtectedRouteProps {
  children: ReactNode;
  allowedPaths?: string[];
}

export function TrialProtectedRoute({ children, allowedPaths = ["/assinatura", "/minha-conta"] }: TrialProtectedRouteProps) {
  const { isTrialExpired, trialEndDate, isLoading, blockReason } = useTrialProtection();
  const navigate = useNavigate();

  useEffect(() => {
    // Só verificar se temos dados completos do trial e não está carregando
    if (!isLoading && trialEndDate && isTrialExpired) {
      const currentPath = window.location.pathname;
      // Permitir qualquer rota que comece com /assinatura (ex: /assinatura, /assinatura/, /assinatura?foo=1)
      const isAssinatura = currentPath.startsWith("/assinatura");
      const isAllowed = isAssinatura || allowedPaths.some(path => currentPath.startsWith(path));
      
      console.log("🚫 Trial expirado - verificando acesso:", {
        currentPath,
        isAllowed,
        isTrialExpired,
        trialEndDate,
        isLoading
      });
      
      if (!isAllowed) {
        console.log("🚫 Acesso negado - redirecionando para assinatura");
        navigate("/assinatura", { replace: true });
      }
    }
  }, [isTrialExpired, trialEndDate, isLoading, navigate, allowedPaths]);

  // Mostrar loading se ainda está carregando dados do trial
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-purple-600 font-medium">Carregando dados do trial...</p>
        </div>
      </div>
    );
  }

  // Só mostrar tela de bloqueio se temos certeza de que o trial expirou
  if (trialEndDate && isTrialExpired) {
    const currentPath = window.location.pathname;
    // Permitir QUALQUER rota que comece com /assinatura, independente do status do trial
    const isAssinatura = currentPath.startsWith("/assinatura");
    const isAllowed = isAssinatura || allowedPaths.some(path => currentPath.startsWith(path));
    
    if (!isAllowed) {
      if (blockReason === "subscription") {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center p-8">
              <div className="text-6xl mb-4">⏳</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Assinatura Expirada</h1>
              <p className="text-gray-600 mb-4">
                Seu pagamento não foi confirmado e sua assinatura expirou. Renove para continuar usando o sistema.
              </p>
              <button
                onClick={() => navigate("/assinatura")}
                className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700"
              >
                Renovar Assinatura
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <div className="text-6xl mb-4">🚫</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Trial Expirado</h1>
            <p className="text-gray-600 mb-4">Seu período de teste gratuito expirou.</p>
            <button 
              onClick={() => navigate("/assinatura")}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
            >
              Assinar Agora
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}


